import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { AlertsService } from 'src/shared/services/alerts.service';
import {
  Alerts,
  EquipmentAlerts,
  EventCode,
  Gateway,
  Installations,
  PaginationInfo,
} from '../models/alerts.model';
import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { Unsubscriber } from '../../unsubscriber/unsubscriber.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Installation } from '../../alert-rules/models/installation.model';
import Swal from 'sweetalert2';
import { MatPaginator } from '@angular/material/paginator';
import jsPDF from 'jspdf';
import {
  tzConvertISOEndDate,
  tzConvertISOStartDate,
  tzConvertUTC2Local,
} from 'src/assets/convertTimeZone/convertTimeZone';

// Imports Standalone
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { CircleNumberSubstepComponent } from 'src/shared/helper-components/circle-number-substep/circle-number-substep.component';
import { AlertEditComponent } from "./alert-edit/alert-edit.component";
import { TableHeaderComponent } from "src/shared/helper-components/table-header/table-header.component";
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { DatePicker } from 'primeng/datepicker';
import { ActivatedRoute } from '@angular/router';
@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  styleUrls: ['./alerts.component.less'],
  imports: [
    CommonModule,
    PageHeaderComponent,
    AlertEditComponent,
    TableHeaderComponent,
    CircleNumberSubstepComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    DialogModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DatePicker,
    CardModule,
    FileUploadModule,
    TagModule,
    TranslateModule,
  ],
})
export class AlertsComponent extends Unsubscriber implements OnInit {
  constructor(
    private alertsService: AlertsService,
    private errorLibService: ErrorLibService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
  ) {
    super();
  }

  public alertType: 'ALERTS' | 'ALERTS_EQUIPMENT' = 'ALERTS';

  // Armazena a empresa selecionada para controle de regras de alerta para Iluminação Paulistana
  public company = localStorage.getItem('lastCompanySelected') || '';

  ngOnInit(): void {
    this.alertType = this.route.snapshot.data['alertType'];

    this.filterForm.get('alertsType')?.setValue(this.alertType);

    this.onAlertsTypeSelected();
  }

  /** Propriedade assincrona que armazena um observable de loading das regras de alertas **/
  public loadingAlertRules$ = this.alertsService.loadingAlertRules$;

  /** Propriedade assincrona que armazena um observable com as regras de alertas **/
  public alertRules = [
    {
      code: ['EQSC'],
      description:
        'Sem Comunicação',
    },
    {
      code: ['AD01', 'BD01', 'CD01', 'DD01'],
      description:
        'Luminária acesa de dia com relé atracado e com presença de corrente',
    },
    {
      code: ['AD02', 'CD02', 'DD02'],
      description:
        'Luminária acesa de dia com relé desatracado e com presença de corrente',
    },
    {
      code: ['AN01', 'BN01', 'CN01', 'DN01'],
      description:
        'Luminária apagada a noite com relé atracado e sem presença de corrente',
    },
    {
      code: ['AN02', 'BN02', 'CN02', 'DN02'],
      description:
        'Luminária apagada a noite com relé desatracado e sem presença de corrente',
    },
  ];

  /** Propriedade que armazena as regras de alertas específicas para a empresa Iluminação Paulistana **/
  public alertsRulesIluminaSP = [
    {
      code: ['NF2'],
      description:
        'Alerta de luminária apagada a noite com relé desatracado e sem presença de corrente',
    },
    {
      code: ['DF2'],
      description:
        'Alerta de luminária acesa de dia com relé atracado e com presença de corrente',
    },
  ];

  /** Propriedade utilizada para manipular quando o formulário de filtro será fechado ou não **/
  public filter: boolean = false;

  public timeRange: string = '24';

  /** Campos do formulário de filtro **/
  public filterForm: FormGroup = this.formBuilder.group({
    installationSearch: [null],
    installationReferenceSelected: [null],
    gatewaySearch: [null],
    gatewaySelected: [null],
    installationSelectedId: [null],
    alertsType: [null],
    alertState: [null],
    status: [null],
    alertCode: [null],
    alertDateTime: [null],
  });

  /** Propriedade que manipula loading de filtro de alertas **/
  public isFiltering: boolean = false;

  /** Utilizado para manipular quais campos do filtro serão exibidos (de acordo com o tipo de alerta selecionado) **/
  public isFieldVisible: boolean = false;

  /** Armazena o id do alerta selecionado para edição**/
  public editedAlert: string = '';
  public editedInstallation: any;

  /** Propriedade que manipula quando o modo de edição é habilitado ou não **/
  public isEditingAlert: boolean = false;

  /** Armazena a lista de gateways **/
  public gatewayList: Gateway[] = [];

  /** Armazena a lista de instalações **/
  public installationList: Installation[] = [];

  /** Propriedade de loading do filtro de gateways **/
  public isLoadingGateway: boolean = false;

  /** Propriedade de loading do filtro de instalações **/
  public isLoadingInstallation: boolean = false;

  /** Propriedade utilizada para manipular quando colunas especificas dos alertas de equipamento deverão aparecer **/
  public columnsEquipmentAlerts: boolean = false;

  /** Variáveis utilizadas para geração e manipulação dos arquivos PDF e CSV **/
  public FILE_DATA: any[] = [];
  public isReady = false;

  /** Propriedade que manipula o loading de download dos arquivos csv/pdf **/
  public isFileLoading: boolean = false;

  /** Armazena os dados de alertas que serão exibidos na tabela **/
  public ELEMENT_DATA: any[] = [];

  /** Armazena a lista com as colunas da tabela incicial **/
  public displayedColumns: string[] = [
    'date',
    'point',
    'rule',
    'occurrences',
    'status',
    'actions',
  ];

  /**Propriedade que renderiza os alertas da lista ELEMENT_DATA para a tabela **/
  public dataSource = new MatTableDataSource<any>(this.ELEMENT_DATA);

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  /**
   * Função que configura o paginator.
   */
  public paginationProperties: PaginationInfo = new PaginationInfo(
    0,
    0,
    false,
    false,
    null,
    null,
  );

  /** Variáveis utilizadas para manipulação do Paginator **/
  public currentPage: number = 0;
  public pageIndex: number = 0;
  public pageSize: number = 1000;
  public length: number = 1;
  public previousPageIndex: number = 0;

  /** Método e variáveis utilizadas no paginator **/
  public setDataSourceAttributes() {
    this.dataSource.data = this.ELEMENT_DATA;
  }

  /** Função chamada toda vez que ocorre um evento no paginator **/
  public pageChanged(event: any) {
    /** Atualiza o valor da página anterior para a página atual **/
    this.previousPageIndex = this.pageIndex;

    this.pageIndex = event.first;

    if (this.filterForm.value.alertsType === 'ALERTS') {
      // event.first já é o offset direto
      this.filterAlerts(null, event.first);
    } else {
      // EquipmentAlerts mantém lógica de cursor
      const isPreviousPage = event.first < this.previousPageIndex;

      if (isPreviousPage) {
        this.filterEquipmentAlerts(this?.paginationProperties?.startCursor);
      } else {
        this.filterEquipmentAlerts(this?.paginationProperties?.endCursor);
      }
    }
  }

  /** Método que exibe/oculta o formulário de filtro, conforme clique do usuário **/
  public filterBox(): void {
    this.filter = !this.filter;
  }

  /** Método que filtra as instalações por referência **/
  public filterInstallationsByReference(reference: any) {
    /** Limpa a lista antes de realizar um novo filtro **/
    this.installationList = [];

    /** Ativa o loading de instalações **/
    this.isLoadingInstallation = true;

    /** Realiza a requisição para buscar as instalações com a referência passada **/
    this.alertsService
      .getInstallationListByReference(reference)
      .valueChanges.subscribe({
        next: (res: any) => {
          /** Valida se a resposta está carregando ou não possui dados **/
          const hasGraphQLErrors = res.errors?.length > 0 || res.error;

          if (res.loading && !hasGraphQLErrors) {
            return;
          }

          // se retornou com erros explícitos
          if (hasGraphQLErrors || !res.data?.installation) {
            this.isLoadingInstallation = false;
            console.error(res);

            this.notifyListError(res.errors?.[0] || res.error);
            return;
          }


          /** Para cada instalação encontrada na resposta **/
          res?.data?.installation?.edges.forEach((installation: any) => {
            /** Adiciona a instalação na lista **/
            this.installationList.push(
              new Installation(
                installation?.node?.id,
                installation?.node?.reference,
              ),
            );
          });

          /** Desativa o carregamento após o processamento **/
          this.isLoadingInstallation = false;

          /** Caso nenhuma instalação seja encontrada na resposta **/
          if (this.installationList.length === 0) {
            Swal.fire({
              title: 'Instalação não encontrada',
              text: 'Não foi encontrada nenhuma instalação com a referência informada.',
              icon: 'warning',
              confirmButtonText: 'Ok',
            });
          }
        },

        /** Em caso de erro na requisição (ex.: 400/500 retornado pela api) **/
        error: (error: any) => {
          /** Limpa a lista de instalações **/
          this.installationList = [];

          /** Desativa o loading **/
          this.isLoadingInstallation = false;

          /** Exibe mensagem de instalação não encontrada ao usuário **/
          Swal.fire({
            title: 'Instalação não encontrada',
            text: 'Não foi encontrada nenhuma instalação com a referência informada.',
            icon: 'warning',
            confirmButtonText: 'Ok',
          });

          /** Exibe o erro no console para depuração **/
          console.error('Error on a query Installation', error);
        },
      });
  }

  /** Método que filtra os gateways por referência **/
  public filterGatewaysByReference(reference: any) {
    /** Limpa a lista antes de realizar um novo filtro **/
    this.gatewayList = [];

    /** Ativa o loading de gateways **/
    this.isLoadingGateway = true;

    /** Realiza a requisição para buscar os gateways com a referência passada **/
    this.alertsService
      .getGatewaysByReference(reference)
      .valueChanges.subscribe({
        next: (res: any) => {
          const hasGraphQLErrors = res.errors?.length > 0 || res.error;

          if (res.loading && !hasGraphQLErrors) {
            return;
          }
          // se retornou com erros explícitos
          if (hasGraphQLErrors || !res.data?.gatewayInstallation) {
            this.isLoadingGateway = false;
            this.notifyListError(res.errors?.[0] || res.error);

            return;
          }

          /** Para cada gateway encontrado na resposta **/
          res?.data?.gatewayInstallation?.edges.forEach(
            (gatewayInstallation: any) => {
              /** Adiciona o gateway na lista **/
              this.gatewayList.push(
                new Gateway(
                  gatewayInstallation?.node?.id,
                  gatewayInstallation?.node?.reference,
                ),
              );
            },
          );

          /** Desativa o carregamento após o processamento **/
          this.isLoadingGateway = false;
        },

        /** Em caso de erro na requisição **/
        error: (error: any) => {
          /** Exibe um alerta com o erro **/
          this.errorLibService.errorAlert(error);

          /** Desativa o loading **/
          this.isLoadingGateway = false;

          /** Exibe o erro no console para depuração **/
          console.error('Error on a query GatewayInstallation', error);
        },
      });
  }

  public filterAlerts(cursor: string | null, offset: number | null) {
    if (this.alertType === 'ALERTS') {
      this.filterSystemAlerts(offset);
    } else {
      this.filterEquipmentAlerts(cursor);
    }
  }

  public filterSystemAlerts(offset: number | null) {
    this.isFiltering = true;

    this.columnsEquipmentAlerts = false;

    let alertCode =
      !this.filterForm?.value?.alertCode &&
        this.filterForm?.value?.alertState === 'ALERTA'
        ? [
          'AD01',
          'AD02',
          'AN01',
          'AN02',
          'BD01',
          'BN01',
          'BN02',
          'CD01',
          'CD02',
          'CN01',
          'CN02',
          'DD01',
          'DD02',
          'DN01',
          'DN02',
          'EQSC',
        ]
        : this.filterForm?.value?.alertCode;
    this.alertsService
      .filterAlerts(
        this.filterForm?.value?.installationReferenceSelected,
        this.filterForm?.value?.alertDateTime[0],
        this.filterForm?.value?.alertDateTime[1],
        alertCode,
        this.filterForm?.value?.alertState,
        this.pageSize,
        offset,
      )
      .valueChanges.subscribe({
        next: (response: any) => {
          const hasGraphQLErrors = response.errors?.length > 0 || response.error;

          if (response.loading && !hasGraphQLErrors) {
            return;
          }

          // se retornou com erros explícitos
          if (hasGraphQLErrors || !response.data?.unifiedAlert) {
            this.notifyListError(response.errors?.[0] || response.error);
            return;
          }

          /** Atualiza os valores dos campos de paginação **/
          this.paginationProperties = new PaginationInfo(
            response?.data?.unifiedAlert?.count,
            response?.data?.unifiedAlert?.total,
            response?.data?.unifiedAlert?.pageInfo?.hasNextPage,
            response?.data?.unifiedAlert?.pageInfo?.hasPreviousPage,
            response?.data?.unifiedAlert?.pageInfo?.startCursor,
            response?.data?.unifiedAlert?.pageInfo?.endCursor,
          );

          /** Limpa a lista dos alertas **/
          this.ELEMENT_DATA = [];

          /** Atualiza o valor da propriedade que manipula a geração do arquivo **/
          this.isReady = false;

          /** Para cada alerta encontrado **/
          response?.data?.unifiedAlert?.edges?.forEach((alert: any) => {
            /** Adiciona os alertas na lista **/
            this.ELEMENT_DATA.push(
              new Alerts(
                alert?.node?.id,
                new Installations(
                  btoa(`Installation:${alert?.node?.installationId}`),
                  alert?.node?.installationReference,
                  alert?.node?.latitude,
                  alert?.node?.longitude,
                  alert?.node?.deviceMacAddress,
                ),
                tzConvertUTC2Local(alert?.node?.firstAlertDatetime),
                tzConvertUTC2Local(alert?.node?.lastAlertDatetime),
                alert?.node?.alertCode,
                alert?.node?.alertDescription,
                alert?.node?.alertState,
                alert?.node?.occurrences,
                alert?.node?.eventsCount,
              ),
            );

            // Controla o length para o paginator não quebrar sem total da API
            this.length =
              this.ELEMENT_DATA.length === this.pageSize
                ? offset! + this.pageSize + 1
                : offset! + this.ELEMENT_DATA.length;

            /** Atualiza a tabela **/
            this.dataSource.data = this.ELEMENT_DATA;

            /** Desativa o carregamento após o processamento **/
            this.isFiltering = false;
          });

          /** Caso nenhum alerta seja encontrado **/
          if (this.ELEMENT_DATA.length === 0) {
            /** Retorna alerta ao usuário **/
            Swal.fire({
              title: 'Sua busca não retornou resultados',
              text: 'Nenhum resultado para este filtro',
              icon: 'warning',
              confirmButtonText: 'Ok',
            });

            /** Desativa o loading **/
            this.isFiltering = false;
          }
        },

        /** Em caso de erro na requisição **/
        error: (error: any) => {
          /** Exibe mensagem de falha e desativa o loading **/
          this.notifyListError(error);

          /** Exibe o erro no console para depuração **/
          console.error('Error on a query Alert', error);
        },
      });
  }

  /** Método que filtra os alertas **/
  public filterEquipmentAlerts(cursor: string | null): any {
    /** Ativa o loading de filtro **/
    this.isFiltering = true;

    /** Atualiza o valor das colunas especificas dos alertas de equipamento como desabilitado incialmente **/
    this.columnsEquipmentAlerts = true;

    /** Caso o cursor seja nulo **/
    if (cursor === null) {
      /** Atualiza o index da página **/
      this.pageIndex = 0;

      /** Gera o arquivo automaticamente **/
      this.isReady = true;
    }

    /** Limpa a lista antes de realizar um novo filtro **/
    this.ELEMENT_DATA = [];

    /** Limpa a lista que armazena as colunas da tabela **/
    this.displayedColumns = [];

    /** Atualiza as colunas da tabela com os dados referente aos alertas de equipamento **/
    this.displayedColumns = ['date', 'point', 'route', 'eventCode', 'device'];

    /** Realiza a requisição para buscar os alertas de equipamento **/
    this.alertsService
      .getEquipmentAlerts(
        tzConvertISOStartDate(this.filterForm.value.alertDateTime[0]),
        tzConvertISOEndDate(this.filterForm.value.alertDateTime[1]),
        this.filterForm?.value?.gatewaySelected,
        this.filterForm?.value?.installationSelectedId,
        cursor,
        this.pageSize,
      )
      .valueChanges.subscribe({
        next: (response: any) => {

          const hasGraphQLErrors = response.errors?.length > 0 || response.error;

          // se não há erro explícito retornado, apenas aguarda
          if (response.loading && !hasGraphQLErrors) {
            return;
          }

          // se retornou com erros explícitos
          if (hasGraphQLErrors || !response.data?.systemAlerts) {
            this.notifyListError(response.errors?.[0] || response.error);
            return;
          }

          this.paginationProperties = new PaginationInfo(
            response?.data?.systemAlerts?.count,
            response?.data?.systemAlerts?.total,
            response?.data?.systemAlerts?.pageInfo?.hasNextPage,
            response?.data?.systemAlerts?.pageInfo?.hasPreviousPage,
            response?.data?.systemAlerts?.pageInfo?.startCursor,
            response?.data?.systemAlerts?.pageInfo?.endCursor,
          );

          /** Limpa a lista dos alertas **/
          this.ELEMENT_DATA = [];

          /** Armazena o total de elementos da tabela **/
          this.length = response?.data?.systemAlerts?.total;

          /** Atualiza o valor da propriedade que manipula a geração do arquivo **/
          this.isReady = false;

          /** Para cada alerta encontrado **/
          response?.data?.systemAlerts?.edges?.forEach((systemAlerts: any) => {
            /** Adiciona os alertas na lista **/
            this.ELEMENT_DATA.push(
              new EquipmentAlerts(
                systemAlerts?.node?.reference,
                systemAlerts?.node?.route,
                this.checkEquipmentAlertEventCode(systemAlerts?.node),
                systemAlerts?.node?.device?.serialNumber,
                tzConvertUTC2Local(systemAlerts?.node?.datetime),
                systemAlerts?.node?.geoposition?.latitude,
                systemAlerts?.node?.geoposition?.longitude,
              ),
            );

            /** Atualiza a tabela **/
            this.dataSource.data = this.ELEMENT_DATA;

            /** Desativa o carregamento após o processamento **/
            this.isFiltering = false;
          });

          /** Caso nenhum alerta seja encontrado **/
          if (this.ELEMENT_DATA.length === 0) {
            /** Retorna alerta ao usuário **/
            Swal.fire({
              title: 'Sua busca não retornou resultados',
              text: 'Nenhum resultado para este filtro',
              icon: 'warning',
              confirmButtonText: 'Ok',
            });

            /** Desativa o loading **/
            this.isFiltering = false;
          }
        },

        /** Em caso de erro na requisição **/
        error: (error: any) => {
          /** Exibe mensagem de falha e desativa o loading **/
          this.notifyListError(error);
          /** Exibe o erro no console para depuração **/
          console.error('Error on a query SystemAlerts', error);
        },
      });
  }

  /** Método que realiza a conversão dos códigos dos alertas de equipamentos **/
  public checkEquipmentAlertEventCode(node: any): any {
    /** Verifica qual o código de evento retornado da api **/
    switch (node.eventCode) {
      case 'LSTA':
        return new EventCode('LSTA', 'Alerta de estado da lâmpada incoerente.');
      case 'RSTA':
        return new EventCode(' RSTA', 'Alerta de reset do equipamento.');
      case 'OVIA':
        return new EventCode('OVIA', 'Alerta sobre corrente.');
      case 'OVVA':
        return new EventCode('OVVA', 'Alerta sobre tensão.');
      case 'MTZA':
        return new EventCode('MTZA', 'Alerta de medidor zerado.');
      case 'FGNA':
        return new EventCode('FGNA', 'Alerta de rede estrangeira');
      case 'MRST':
        return new EventCode('MRST', 'Alerta de reset do medidor');
      case 'BLKA':
        return new EventCode('BLKA', 'Alerta de lâmpada piscando');
      case 'PFLA':
        return new EventCode('PFLA', 'Alerta de fator de potência baixo');
      default:
        return 'Não definido';
    }
  }

  /** Evento que verifica qual tipo de alerta foi selecionado pelo usuário **/
  public onAlertsTypeSelected(): void {
    /** Caso algum alerta seja selecionado **/
    /** Verifica se o tipo do alerta é alerta comum (alertas do sistema) **/
    if (this.filterForm.value.alertsType === 'ALERTS') {
      /** Habilita os campos especificos para serem exibidos no filtro de alertas **/
      this.isFieldVisible = true;

      /** Remove a validação dos campos de datas (pois esses campos não obrigatórios nessa requisição) **/
      this.filterForm.get('alertDateTime')?.addValidators(Validators.required);
      this.filterForm.get('alertDateTime')?.updateValueAndValidity();

      /** Caso o tipo de alerta selecionado seja os alertas de equipamento **/
    } else if (this.filterForm.value.alertsType === 'ALERTS_EQUIPMENT') {
      /** Desabilita os campos especificos do filtro de alertas de sistema **/
      this.isFieldVisible = false;

      /** Adiciona a validação dos campos de datas (pois esses campos são obrigatórios nessa requisição) **/
      this.filterForm.get('alertDateTime')?.addValidators(Validators.required);
      this.filterForm.get('alertDateTime')?.updateValueAndValidity();
    }
  }
  /** Método utilizado para editar os alertas **/
  public editAlert(alert: string, installation: any): void {
    this.editedAlert = alert;
    this.editedInstallation = installation;
    this.isEditingAlert = true;
  }

  /** Evento disparado ao usuário editar os alertas **/
  public onEditAlert(event: any): void {
    if (event) {
      this.alertsService.resetPaginations();
      this.ELEMENT_DATA = [];
      this.alertsService.filterAlerts(null, null, null, null, null, null, null);
    }
  }

  /** Evento disparado ao usupario tentar cancelar a edição dos alertas **/
  public onCancelEdit(event: any): void {
    if (event) {
      this.isEditingAlert = false;
      this.editedAlert = '';
    }
  }

  /** Exibe a mensagem de falha na geração da lista e finaliza os loadings da tela **/
  private notifyListError(error?: any): void {
    this.isFiltering = false;
    this.isFileLoading = false;

    const status =
      error?.networkError?.error?.status || error?.networkError?.status || null;

    /** Token expirado já é tratado globalmente, não exibe mensagem duplicada **/
    if (status === 401) {
      return;
    }

    this.errorLibService.errorAlert(error);

  }

  /** Método que gera os arquivos csv/pdf — decide qual fluxo executar **/
  public generateFileData(fileType: string) {
    this.isFileLoading = true;
    this.isReady = false;
    this.FILE_DATA = [];

    if (this.filterForm.value.alertsType === 'ALERTS') {
      this.generateAlertsData(0, fileType);
    } else if (this.filterForm.value.alertsType === 'ALERTS_EQUIPMENT') {
      this.generateEquipmentData(null, fileType);
    }
  }

  /** Método responsável por buscar os alertas de sistema (usa offset) **/
  private generateAlertsData(offset: number, fileType: string) {

    let alertCode =
      !this.filterForm?.value?.alertCode &&
        this.filterForm?.value?.alertState === 'ALERTA'
        ? [
          'AD01',
          'AD02',
          'AN01',
          'AN02',
          'BD01',
          'BN01',
          'BN02',
          'CD01',
          'CD02',
          'CN01',
          'CN02',
          'DD01',
          'DD02',
          'DN01',
          'DN02',
          'EQSC',
        ]
        : this.filterForm?.value?.alertCode;

    this.alertsService
      .filterAlerts(
        this.filterForm?.value?.installationReferenceSelected,
        this.filterForm?.value?.alertDateTime[0],
        this.filterForm?.value?.alertDateTime[1],
        alertCode,
        this.filterForm?.value?.alertState,
        100,
        offset,
      )
      .valueChanges.subscribe({
        next: (res: any) => {
          /** Caso retorne um erro parcial **/
          if (!res.data.unifiedAlert) {
            this.notifyListError();
            return;
          }

          /** Valida se a resposta está carregando ou não possui dados **/
          if (res.loading || !res.data) {
            return;
          }

          res?.data?.unifiedAlert?.edges?.forEach((alert: any) => {
            this.FILE_DATA.push(
              new Alerts(
                alert?.node.id,
                new Installations(
                  btoa(`Installation:${alert.node.installationId}`),
                  alert?.node?.installationReference,
                  alert?.node?.latitude,
                  alert?.node?.longitude,
                  alert?.node?.deviceMacAddress,
                ),
                tzConvertUTC2Local(alert?.node?.firstAlertDatetime),
                tzConvertUTC2Local(alert?.node?.lastAlertDatetime),
                alert?.node?.alertCode,
                alert?.node?.alertDescription,
                alert?.node?.alertState,
                alert?.node?.occurrences,
                alert?.node?.eventsCount,
              ),
            );
          });

          /** Última página — dispara o download **/
          if (res?.data?.unifiedAlert?.edges.length < this.pageSize) {
            this.isReady = true;
            this.downloadFile(fileType);
          } else {
            /** Busca a próxima página **/
            this.generateAlertsData(offset + 100, fileType);
          }
        },

        error: (error: any) => {
          /** Na primeira página zera o índice **/
          if (offset === 0) {
            this.pageIndex = 0;
          }

          this.notifyListError(error);
          console.error('Error on download file alerts', error);
        },
      });
  }

  /** Método responsável por buscar os alertas de equipamento (usa cursor) **/
  private generateEquipmentData(cursor: string | null, fileType: string) {
    this.alertsService
      .getEquipmentAlerts(
        tzConvertISOStartDate(this.filterForm.value.alertDateTime[0]),
        tzConvertISOEndDate(this.filterForm.value.alertDateTime[1]),
        this.filterForm?.value?.gatewaySelected,
        this.filterForm?.value?.installationSelectedId,
        cursor,
        100,
      )
      .valueChanges.subscribe({
        next: (res: any) => {
          /** Valida se a resposta está carregando ou não possui dados **/
          if (res.loading || !res.data) {
            return;
          }

          res?.data?.systemAlerts?.edges.forEach((systemAlert: any) => {
            this.FILE_DATA.push(
              new EquipmentAlerts(
                systemAlert?.node?.reference,
                systemAlert?.node?.route,
                this.checkEquipmentAlertEventCode(systemAlert?.node),
                systemAlert?.node?.device?.serialNumber,
                tzConvertUTC2Local(systemAlert?.node?.datetime),
                systemAlert?.node?.geoposition?.latitude,
                systemAlert?.node?.geoposition?.longitude,
              ),
            );
          });

          /** Última página — dispara o download **/
          if (!res?.data?.systemAlerts?.pageInfo?.hasNextPage) {
            this.isReady = true;
            this.downloadFile(fileType);
          } else {
            /** Busca a próxima página **/
            this.generateEquipmentData(
              res?.data?.systemAlerts?.pageInfo?.endCursor,
              fileType,
            );
          }
        },

        error: (error: any) => {
          if (cursor === null) {
            this.pageIndex = 0;
          }

          this.notifyListError(error);
          console.error('Error on download file alerts', error);
        },
      });
  }

  /** Método que efetua o download do arquivo gerado **/
  private downloadFile(fileType: string) {
    this.isFileLoading = false;
    if (fileType === 'PDF') {
      this.downloadPDF();
    } else {
      this.downloadCSV();
    }
  }

  // Função que faz a conversão e substitui , por ;
  private convertToCSV(arr: any): string {
    const content = [Object.keys(arr[0])].concat(arr);
    // Índice da coluna que tem a vírgula (algumas empresas usam no nome da regra)
    const indexColumnComma = 2;

    return content
      .map((item) => {
        const values = Object.values(item);
        return values
          .map((_, index) => {
            const rowValue = values[index]?.toString();
            return index === indexColumnComma
              ? rowValue
              : rowValue?.replace(/,/g, ';');
          })
          .join(';');
      })
      .join('\n');
  }

  /** Método que lê o arquivo csv **/
  public downloadCSV(): void {
    /** Variável que armazena a lista com o conteúdo do arquivo **/
    let content: any = [];

    /** Verifica se o tipo de alerta selecionado é de alertas do sistema **/
    if (this.filterForm.value.alertsType === 'ALERTS') {
      /** Para cada elemento da lista filtrada **/
      this.FILE_DATA.forEach((alert) => {
        /** Armazena na lista de conteúdo do arquivo **/
        content.push({
          'Primeiro Alerta': alert?.firstAlertDatetime?.replace(',', ' '),
          'Último Alerta': alert?.lastAlertDatetime?.replace(',', ' '),
          Instalação: alert?.installation?.reference,
          Latitude: alert?.installation?.latitude,
          Longitude: alert?.installation?.longitude,
          'Regra de alerta': alert?.alertDescription,
          Status: alert?.alertState,
        });
      });
      /** Verifica se o tipo de alerta selecionado é de alertas do equipamento **/
    } else if (this.filterForm.value.alertsType === 'ALERTS_EQUIPMENT') {
      /** Para cada elemento da lista filtrada **/
      this.FILE_DATA.forEach((alert) => {
        /** Armazena na lista de conteúdo do arquivo **/
        content.push({
          'Data do Alerta': alert?.alertDatetime.replace(',', ' '),
          Instalação: alert?.reference,
          Latitude: alert?.latitude,
          Longitude: alert?.longitude,
          Concentrador: alert?.route,
          'Código do Evento':
            alert?.eventCode?.cod + ' - ' + alert?.eventCode?.description,
          Equipamento: alert?.device,
        });
      });
    }

    /** Adiciona o conteúdo convertido dentro de uma nova variável. **/
    let data = this.convertToCSV(content);

    /**  Transforma o conteúdo para o formato blob para geração do arquivo. **/
    let file = new Blob([data], { type: 'text/csv' });

    /** Cria um endereçamento para o arquivo. **/
    let url = window.URL.createObjectURL(file);

    /** Cria um elemento para a linkagem do arquivo. **/
    let link = document.createElement('a');

    /** Adiciona a url no elemento de linkagem. **/
    link.href = url;

    /**  Nomeia o arquivo que será feito o download. **/
    link.download = `${new Date().toLocaleDateString()}-${new Date().getHours()}:${new Date().getMinutes()}-alerts.csv`;

    /** Adiciona o link no documento. **/
    document.body.appendChild(link);

    /** Atica o evento de click para realizar o download. **/
    link.click();

    /** Remove o link do documento. **/
    document.body.removeChild(link);

    /** Remove o endereço do download. **/
    window.URL.revokeObjectURL(url);

    this.FILE_DATA = [];
    this.isReady = false;
  }

  /** Método que lê o arquivo pdf **/
  public downloadPDF(): void {
    /** Váriável que contém o cabeçalho do arquivo **/
    let header: any[] = [];
    /** Variável que armazena o conteúdo do arquivo **/
    let content: any = [];

    /** Verifica se o tipo de alerta selecionado é de alertas do sistema **/
    if (this.filterForm.value.alertsType === 'ALERTS') {
      /** Atualiza o cabeçalho do arquivo **/
      header = [
        'Primeiro Alerta',
        'Último Alerta',
        'Instalação',
        'Regra de alerta',
        'Ocorrências',
        'Status',
      ];

      /** Para cada elemento da lista filtrada **/
      this.FILE_DATA.forEach((alert) => {
        /** Armazena na lista de conteúdo do arquivo **/

        content.push([
          alert?.firstAlertDatetime,
          alert?.lastAlertDatetime,
          alert?.installation?.reference,
          alert?.alertDescription,
          alert?.occurrences,
          alert?.alertState,
        ]);
      });

      /** Verifica se o tipo de alerta selecionado é de alertas do equipamento **/
    } else if (this.filterForm.value.alertsType === 'ALERTS_EQUIPMENT') {
      /** Atualiza o cabeçalho do arquivo **/
      header = [
        'Data do Alerta',
        'Instalação',
        'Concentrador',
        'Código do Evento',
        'Equipamento',
      ];

      /** Para cada elemento da lista filtrada **/
      this.FILE_DATA.forEach((alert) => {
        /** Armazena na lista de conteúdo do arquivo **/
        content.push([
          alert?.alertDatetime,
          alert?.reference,
          alert?.route,
          alert?.eventCode?.cod + ' - ' + alert?.eventCode?.description,
          alert?.device,
        ]);
      });
    }

    /** Define a orientação da tabela (paisagem nesse caso) **/
    const orientation = 'l';

    /** Cria o objeto da lib jsPDF com a orientação e tamanho da página**/
    const doc: any = new jsPDF(orientation, 'mm', 'a4');

    /**  Cria a tabela com o tableLayout como header e a a lista de conteúdo como corpo da tabela **/
    doc.autoTable({
      theme: 'grid',
      margin: { top: 20 },
      head: [header],
      body: content,
    });

    /** Baixa o arquivo para o usuário, definindo o nome com a data de download **/
    doc.save(
      `${new Date().toLocaleDateString()}-${new Date().getHours()}${new Date().getMinutes()}${new Date().getSeconds()}-alerts.pdf`,
    );

    /** Limpa a lista filtrada **/
    this.FILE_DATA = [];
    this.isReady = false;
  }
}
